import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

/**
 * Everything the app does once at launch, shared by every front end (the Compose [App] and the
 * native SwiftUI app on iOS) so both boot identically: seed persisted state, configure purchases,
 * warm the rewarded ad, and run the remote checks. Safe to run again (Compose re-runs it when the
 * Android activity is recreated); every step is idempotent. [scope] must live as long as the UI
 * that started it — the limited-time dice capture keeps listening for entitlement changes, and
 * every refresh (each return to the foreground) emits, so an event that began while the app was
 * suspended is picked up with the refreshed Plus status.
 */
object AppStartup {
    fun start(playerPreferences: PlayerPreferences, scope: CoroutineScope) {
        LimitedTimeDiceEntitlements.init(playerPreferences)
        scope.launch {
            RemoveAdsPurchases.uiState.collect { captureLimitedTimeDice(it) }
        }

        Analytics.appOpen()
        RemoveAdsPurchases.configure(
            adsRemovedHint = playerPreferences.getAdsRemovedCached(),
            onAdsRemovedChanged = playerPreferences::setAdsRemovedCached,
        )
        Achievements.init(playerPreferences)
        PlusPass.init(playerPreferences)
        DiceTrial.init(playerPreferences)
        OnboardingHints.init(playerPreferences)
        CareerStatsStore.init(playerPreferences)
        // Fetch the rewarded ad early so the store offers are ready on first tap
        // (loading only when the store opened made the first tap race the network and fail).
        BankoRewardedAd.preload()
        scope.launch {
            unlockCreatorDiceForCurrentFirebaseUser()
            AppUpdateGate.refresh()
        }
    }

    private fun captureLimitedTimeDice(purchaseState: RemoveAdsPurchaseUiState) {
        if (purchaseState.entitlementsResolved) {
            LimitedTimeDiceEntitlements.captureEligibleRewards(
                bankoPlusActive = purchaseState.bankoPlusActive,
            )
        }
    }
}
